import {PixIcon} from "../../shared/components/icons";

/**
 * Describes each editable API resource: used by the generic create/edit pages.
 *
 * Field options: name (API field), label, type (text|tel|email|textarea|
 * number|select|time|tags), required, nullable (send null when empty), maxLength,
 * step (number inputs), options ([{value, label}] for select), help.
 *
 * @typedef {{
 *   name: string,
 *   label: string,
 *   type: "text"|"tel"|"email"|"textarea"|"number"|"select"|"time"|"tags",
 *   required?: boolean,
 *   nullable?: boolean,
 *   maxLength?: number,
 *   step?: string,
 *   options?: {value: string, label: string}[],
 *   help?: string,
 *   placeholder?: string,
 *   icon?: React.ComponentType,
 * }} FieldDef
 * @typedef {{singular: string, url: string, path: string, fields: FieldDef[]}} Resource
 */

/** @type {Resource} */
export const clients = {
    singular: "cliente",
    url: "/os/api/clients/",
    path: "/clients",
    fields: [
        {name: "name", label: "Nome", type: "text", required: true, maxLength: 128},
        {name: "phone", label: "Telefone", type: "tel", required: true, maxLength: 20},
        {name: "alternate_phone", label: "Telefone alternativo", type: "tel", nullable: true, maxLength: 20},
        {name: "email", label: "E-mail", type: "email", nullable: true, maxLength: 64},
        {name: "address", label: "Endereço", type: "text", nullable: true, maxLength: 256},
    ],
};

/** @type {Resource} */
export const serviceItems = {
    singular: "serviço",
    url: "/os/api/service_items/",
    path: "/service-items",
    fields: [
        {name: "name", label: "Nome", type: "text", required: true, maxLength: 64},
        {name: "description", label: "Descrição", type: "textarea", required: true},
        {name: "base_value", label: "Valor base", type: "number", required: true},
        {
            name: "cost",
            label: "Custo",
            type: "number",
            required: true,
            help: "Quanto custa para você fazer o serviço (material, mão de obra). O cliente não vê o custo. Use 0 se não houver.",
        },
        {
            name: "status",
            label: "Situação",
            type: "select",
            required: true,
            options: [
                {value: "AC", label: "Ativo"},
                {value: "IN", label: "Inativo"},
            ],
        },
    ],
};

/** @type {Resource} */
export const products = {
    singular: "produto",
    url: "/os/api/products/",
    path: "/products",
    fields: [
        {name: "name", label: "Nome", type: "text", required: true, maxLength: 64},
        {name: "description", label: "Descrição", type: "textarea"},
        {name: "base_value", label: "Valor base", type: "number", required: true},
        {
            name: "cost",
            label: "Custo",
            type: "number",
            required: true,
            help: "Quanto o produto custa para você. O cliente não vê o custo. Use 0 se não houver.",
        },
        {
            name: "status",
            label: "Situação",
            type: "select",
            required: true,
            options: [
                {value: "AC", label: "Ativo"},
                {value: "IN", label: "Inativo"},
            ],
        },
    ],
};

/** @type {Resource} */
export const deviceTypes = {
    singular: "tipo de equipamento",
    url: "/os/api/device_types/",
    path: "/device-types",
    fields: [
        {name: "name", label: "Nome", type: "text", required: true, maxLength: 128},
        {name: "description", label: "Descrição", type: "textarea", required: true},
        {
            name: "fields_list",
            label: "Campos",
            type: "tags",
            nullable: true,
            maxLength: 512,
            placeholder: "Digite um campo e pressione Enter",
            help: "Pressione Enter ou vírgula para adicionar cada campo. Sem campos, deixe em branco.",
        },
    ],
};

// The provider config is a singleton edited at /config/edit (see
// pages/configEdit.jsx), so it has no generic new/detail routes.
/** @type {Resource} */
export const config = {
    singular: "configurações",
    url: "/os/api/provider_config/",
    path: "/config",
    fields: [
        {name: "name_on_receipt", label: "Nome no recibo", type: "text", nullable: true, maxLength: 256},
        {
            name: "pix_key",
            label: "Chave Pix",
            icon: PixIcon,
            type: "text",
            nullable: true,
            maxLength: 77,
            help: "CPF/CNPJ, e-mail, telefone ou chave aleatória. Usada nos links de pagamento.",
        },
        {name: "address", label: "Endereço", type: "text", nullable: true, maxLength: 256},
        {name: "city", label: "Cidade", type: "text", nullable: true, maxLength: 256},
        {
            name: "about",
            label: "Sobre",
            type: "textarea",
            nullable: true,
            maxLength: 2000,
            help: "Aparece na sua página pública, para os clientes conhecerem o seu trabalho.",
        },
        {
            name: "language",
            label: "Idioma",
            type: "select",
            required: true,
            options: [
                {value: "pt-br", label: "Português (Brasil)"},
                {value: "en", label: "Inglês (EUA)"},
            ],
        },
        {
            name: "currency",
            label: "Moeda",
            type: "select",
            required: true,
            options: [
                {value: "R$", label: "Real (R$)"},
                {value: "US$", label: "Dólar americano (US$)"},
                {value: "€", label: "Euro (€)"},
            ],
        },
        {
            name: "timezone",
            label: "Fuso horário",
            type: "select",
            required: true,
            options: Intl.supportedValuesOf("timeZone").map((tz) => ({value: tz, label: tz})),
        },
        {name: "opening_time", label: "Horário de abertura", type: "time", required: true},
        {name: "closing_time", label: "Horário de fechamento", type: "time", required: true},
        {name: "quote_expiration", label: "Validade do orçamento (dias)", type: "number", step: "1", required: true},
        {
            name: "quote_availability",
            label: "Disponibilidade do orçamento (dias)",
            type: "number",
            step: "1",
            required: true,
        },
    ],
};

// Pre-filled values for the "new" form (API defaults worth surfacing).
export const newDefaults = {
    [serviceItems.url]: {status: "AC", cost: "0"},
    [products.url]: {status: "AC", cost: "0"},
    [deviceTypes.url]: {fields_list: "serial,"},
};
